/**
 * Uploaded files as mini previews that open full size in a lightbox.
 *
 * Replaces "View promotional graphic" links. A thumbnail of the actual file answers
 * "what is this?" before the click, which a text link never can — and opening it in
 * place keeps the reader in the drawer they were reading rather than sending them to
 * a new tab and back.
 *
 * Reusable on purpose: the speaker drawer and the event modal show the same kind of
 * thing, and two lightboxes would drift.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, FileText, PlayCircle, X, ZoomIn } from "lucide-react";
import ResponsiveImage from "@/components/ResponsiveImage";

export type Attachment = {
  url: string;
  /** Describes the file for assistive tech and the lightbox caption. */
  label: string;
  /** Drives whether this renders as an image or a document. Images are assumed. */
  contentType?: string | null;
};

export const isPdf = (attachment: Attachment) => attachment.contentType === "application/pdf"
  || /\.pdf($|\?)/i.test(attachment.url);

/**
 * Video, by declared type or by extension.
 *
 * Extension is the fallback because `contentType` is only recorded for files this
 * product uploaded; anything referenced by URL arrives without one.
 */
export const isVideo = (attachment: Attachment) => attachment.contentType?.startsWith("video/")
  || /\.(mp4|webm|mov|m4v)($|\?)/i.test(attachment.url);

export default function AttachmentPreviews({
  attachments,
  heading,
  variant = "compact",
}: {
  attachments: Attachment[];
  heading?: string;
  variant?: "compact" | "gallery";
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Absent rather than an empty strip, so a speaker with no uploads renders nothing.
  if (attachments.length === 0) return null;

  return (
    <>
      {heading && <p className={`attachment-previews__heading attachment-previews__heading--${variant}`}>{heading}</p>}
      <ul className={`attachment-previews attachment-previews--${variant}`}>
        {attachments.map((attachment, index) => {
          const kind = isVideo(attachment) ? "video" : isPdf(attachment) ? "pdf" : "image";

          return (
          <li key={attachment.url}>
            <button
              type="button"
              className={`attachment-previews__item attachment-previews__item--${variant}`}
              onClick={() => setOpenIndex(index)}
              aria-label={`View ${attachment.label} full size`}
            >
              <span className="attachment-previews__visual" aria-hidden="true">
                {variant === "gallery" && (
                  <span className={`attachment-previews__kind attachment-previews__kind--${kind}`}>{kind.toUpperCase()}</span>
                )}
                {variant === "gallery" && (
                  <span className="attachment-previews__open">
                    {kind === "video" ? <PlayCircle size={22} /> : <ExternalLink size={20} />}
                  </span>
                )}
                {isVideo(attachment) ? (
                  <span className="attachment-previews__doc"><PlayCircle size={variant === "gallery" ? 28 : 20} /></span>
                ) : isPdf(attachment) ? (
                  <span className="attachment-previews__doc"><FileText size={variant === "gallery" ? 26 : 18} /></span>
                ) : (
                  /* The real uploaded image is kept as the visual preview. */
                   <ResponsiveImage src={attachment.url} alt="" loading="lazy" maxWidth={variant === "gallery" ? 640 : 96} sizes={variant === "gallery" ? "(max-width: 420px) 100vw, 50vw" : "64px"} />
                )}
              </span>
              {variant === "gallery" ? (
                <span className="attachment-previews__gallery-copy">
                  <span className="attachment-previews__label">{attachment.label}</span>
                </span>
              ) : (
                <span className="attachment-previews__zoom" aria-hidden="true"><ZoomIn size={13} /></span>
              )}
            </button>
          </li>
          );
        })}
      </ul>

      {openIndex !== null && attachments[openIndex] && (
        <MediaLightbox attachment={attachments[openIndex]} onClose={() => setOpenIndex(null)} />
      )}

      <style>{`
        .attachment-previews__heading {
          margin: 0 0 .45rem;
          color: #8190a5;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 750;
          letter-spacing: .06em;
          text-transform: uppercase;
        }
        .attachment-previews { display: flex; flex-wrap: wrap; gap: .5rem; margin: 0; padding: 0; list-style: none; }
        .attachment-previews--gallery { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:.88rem; }
        .attachment-previews__item {
          position: relative;
          display: block;
          width: 64px;
          height: 64px;
          padding: 0;
          border: 1px solid rgba(255,255,255,.14);
          border-radius: .55rem;
          background: #05070d;
          overflow: hidden;
          cursor: zoom-in;
          transition: border-color 160ms ease, transform 160ms ease;
        }
        .attachment-previews__item:hover,
        .attachment-previews__item:focus-visible { border-color: #e91e8c; transform: translateY(-1px); }
        .attachment-previews__visual { display:block; width:100%; height:100%; overflow:hidden; }
        .attachment-previews__item img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .attachment-previews__doc { display: grid; place-items: center; width: 100%; height: 100%; color: #94a3b8; }
        .attachment-previews__zoom {
          position: absolute;
          right: 3px;
          bottom: 3px;
          display: grid;
          place-items: center;
          width: 20px;
          height: 20px;
          border-radius: .3rem;
          background: rgba(5,7,13,.78);
          color: #f9a8d4;
        }
        .attachment-previews__item--gallery { width:100%; height:auto; min-height:13.2rem; display:flex; flex-direction:column; align-items:stretch; border-radius:.82rem; background:linear-gradient(155deg, rgba(20,29,49,.94), rgba(7,11,22,.96)); box-shadow:0 12px 28px rgba(0,0,0,.2); text-align:left; cursor:pointer; }
        .attachment-previews__item--gallery .attachment-previews__visual { position:relative; height:auto; aspect-ratio:16/9; background:linear-gradient(145deg, rgba(54,65,91,.76), rgba(9,14,27,.98)); }
        .attachment-previews__item--gallery .attachment-previews__visual::after { position:absolute; z-index:1; inset:0; content:""; pointer-events:none; background:linear-gradient(180deg, rgba(5,8,16,.03) 24%, rgba(5,8,16,.7) 100%); }
        .attachment-previews__item--gallery .attachment-previews__doc { color:#e8d3ff; background:radial-gradient(circle at 50% 35%, rgba(233,30,140,.26), transparent 50%); }
        .attachment-previews__gallery-copy { position:relative; display:grid; gap:.38rem; min-height:4.6rem; padding:.82rem .88rem .92rem; }
        .attachment-previews__kind { position:absolute; z-index:3; top:.78rem; left:.78rem; display:inline-flex; min-height:1.85rem; align-items:center; width:max-content; padding:.4rem .72rem; border:1px solid rgba(255,255,255,.23); border-radius:.42rem; color:#fff; font-family:'DM Sans','Inter',sans-serif; font-size:.7rem; font-weight:850; letter-spacing:.075em; line-height:1; box-shadow:inset 0 1px 0 rgba(255,255,255,.24), 0 8px 18px rgba(0,0,0,.28); }
        .attachment-previews__kind--pdf { background:linear-gradient(115deg, #E91E8C, #BE2B70); }
        .attachment-previews__kind--video { background:linear-gradient(115deg, #087F92, #16BBD0); }
        .attachment-previews__kind--image { background:linear-gradient(115deg, #8D55D9, #B674EB); }
        .attachment-previews__label { display:-webkit-box; overflow:hidden; color:#f8fafc; font-family:'Inter',sans-serif; font-size:.82rem; font-weight:650; line-height:1.38; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
        .attachment-previews__open { position:absolute; z-index:3; top:.66rem; right:.66rem; display:grid; width:2.7rem; height:2.7rem; place-items:center; border:1px solid rgba(232,240,255,.55); border-radius:50%; color:#fff; background:rgba(4,9,19,.56); box-shadow:0 8px 22px rgba(0,0,0,.3), inset 0 1px 0 rgba(255,255,255,.12); backdrop-filter:blur(9px); -webkit-backdrop-filter:blur(9px); }
        .attachment-previews__open svg { filter:drop-shadow(0 0 6px rgba(255,255,255,.28)); }
        .attachment-previews__item--gallery:hover, .attachment-previews__item--gallery:focus-visible { border-color:rgba(125,234,255,.82); box-shadow:0 12px 26px rgba(0,0,0,.3), 0 0 0 .16rem rgba(125,234,255,.12); transform:translateY(-2px); outline:none; }
        @media (max-width:420px) { .attachment-previews--gallery { grid-template-columns:1fr; } }
      `}</style>
    </>
  );
}

/**
 * The full-size view.
 *
 * Escape closes and the backdrop closes, because a viewer with only a small X is a
 * trap on a phone. Focus moves to the close button on open and returns to whatever
 * opened it on close, so the keyboard does not get stranded behind the overlay.
 */
export function MediaLightbox({ attachment, onClose }: { attachment: Attachment; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusTo = useRef<Element | null>(null);

  useEffect(() => {
    returnFocusTo.current = document.activeElement;
    closeRef.current?.focus();

    // The page behind must not scroll while a full-screen overlay is open.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
      (returnFocusTo.current as HTMLElement | null)?.focus?.();
    };
  }, []);

  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={attachment.label}
      className="attachment-lightbox"
      onClick={onClose}
      onKeyDown={onKeyDown}
    >
      {/* Stops a click on the file itself from closing the viewer. */}
      <div className="attachment-lightbox__stage" onClick={event => event.stopPropagation()}>
        {isPdf(attachment) ? (
          /*
            Not sandboxed — deliberately, and after trying. A sandboxed frame disables
            plugin-class renderers, and Chrome's PDF viewer is one: with any `sandbox`
            attribute at all, scripts allowed or not, the frame shows Chrome's blocked
            placeholder instead of the document. So the protection lives on the
            server rather than on this element. Every PDF that reaches here as a PDF
            is an event document, and those are refused at upload if they carry any
            script, attachment or launch action, then served with a sandboxing CSP.
            (Speaker flyers never arrive as PDFs: that pipeline rasterises them.) The
            link below is the escape hatch for a browser with no inline viewer.
          */
          <>
            <iframe
              className="attachment-lightbox__pdf"
              src={attachment.url}
              title={attachment.label}
            />
            <a className="attachment-lightbox__download" href={attachment.url} target="_blank" rel="noopener noreferrer">
              Open {attachment.label} in a new tab
            </a>
          </>
        ) : isVideo(attachment) ? (
          /*
            `controls` and nothing else: no autoplay, because a video that starts
            talking when a lightbox opens is startling and, on a metered connection,
            expensive. `preload="metadata"` fetches enough for the duration and a
            first frame without pulling the whole file down.
          */
          <video
            className="attachment-lightbox__video"
            src={attachment.url}
            controls
            preload="metadata"
            playsInline
            aria-label={attachment.label}
          />
        ) : (
          <ResponsiveImage className="attachment-lightbox__image" src={attachment.url} alt={attachment.label} loading="eager" maxWidth={1280} sizes="min(1100px, 100vw)" />
        )}
      </div>

      <button ref={closeRef} type="button" className="attachment-lightbox__close" onClick={onClose} aria-label="Close">
        <X size={18} aria-hidden="true" />
      </button>

      <style>{`
        .attachment-lightbox {
          position: fixed;
          inset: 0;
          z-index: 400;
          display: grid;
          place-items: center;
          padding: clamp(1rem, 4vw, 3rem);
          background: rgba(3,5,10,.88);
          backdrop-filter: blur(6px);
        }
        .attachment-lightbox__stage { display: grid; gap: .6rem; justify-items: center; max-width: 100%; max-height: 100%; }
        .attachment-lightbox__image {
          display: block;
          max-width: min(1100px, 100%);
          max-height: 82vh;
          border-radius: .6rem;
          box-shadow: 0 30px 90px rgba(0,0,0,.5);
        }
        .attachment-lightbox__video {
          display: block;
          width: min(1100px, 92vw);
          max-height: 82vh;
          border-radius: .6rem;
          background: #000;
          box-shadow: 0 30px 90px rgba(0,0,0,.5);
        }
        .attachment-lightbox__pdf {
          width: min(1000px, 92vw);
          height: 82vh;
          border: 0;
          border-radius: .6rem;
          background: #fff;
        }
        .attachment-lightbox__download {
          color: #7deaff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 700;
        }
        .attachment-lightbox__close {
          position: absolute;
          top: clamp(.6rem, 2vw, 1.2rem);
          right: clamp(.6rem, 2vw, 1.2rem);
          display: grid;
          place-items: center;
          width: 40px;
          height: 40px;
          border: 1px solid rgba(255,255,255,.2);
          border-radius: 50%;
          background: rgba(13,18,32,.9);
          color: #e2e8f0;
          cursor: pointer;
        }
        .attachment-lightbox__close:hover { border-color: #e91e8c; color: #f9a8d4; }
      `}</style>
    </div>
  );
}
